import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { SelectOption } from '../Select';
import { Kato } from '@app/models';
import { getKatoList, getParentKatoList } from '@app/api';
import { KatoModal } from '@app/modals';
import { cn } from 'lib/utils';
import { ChevronDown } from 'lucide-react';

interface KatoSelectProps {
  parentNumber?: number;
  onChange: (value: SelectOption<Kato>) => void;
  className?: string;
}

function KatoSelect(props: KatoSelectProps) {
  const { parentNumber = null, onChange, className } = props;
  const [pending, setPending] = useState<boolean>(true);
  const [option, setOption] = useState<SelectOption<Kato> | null>(null);
  const [options, setOptions] = useState<SelectOption<Kato>[]>([]);

  const onChangeSelect = useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find(
        (opt) => opt.value.id === selectedValue
      );
      setOption(selectedOption || null);
      onChange(selectedOption as SelectOption<Kato>);
    },
    [onChange, options]
  );

  const getData = useCallback(async () => {
    try {
      const katoList = !parentNumber
        ? await getParentKatoList()
        : await getKatoList({
            parentNumber,
          });

      const newOptions = katoList.map((item) => ({
        label: item.nameRu,
        value: item,
      }));

      setOptions(newOptions);

      if (!parentNumber && newOptions.length > 0) {
        const kazakhstanOption = newOptions.find(
          (opt) => opt.value.number === 74613922
        );
        if (kazakhstanOption) {
          setOption(kazakhstanOption);
          onChange(kazakhstanOption);
        }
      }

      setPending(false);
    } catch (e) {
      setPending(false);
    }
  }, [parentNumber, onChange]);

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, [parentNumber, getData]);

  if (!pending && options.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-2">
        <Label>Выберите раздел</Label>
        <Select value={option?.value.id || ''} onValueChange={onChangeSelect}>
          <SelectTrigger>
            <SelectValue placeholder="Выберите" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value.id} value={option.value.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {option && (
        <KatoSelect parentNumber={option.value.number} onChange={onChange} />
      )}
    </div>
  );
}

interface KatoSelectTriggerProps {
  label?: string;
  placeholder?: string;
  value?: string;
  error?: boolean;
  helperText?: string;
  onChange: (value: SelectOption<Kato>, name: string) => void;
  name?: string;
  onClickClear?: () => void;
  className?: string;
  labelClassName?: string;
}

export function KatoSelectTrigger(props: KatoSelectTriggerProps) {
  const {
    label,
    placeholder,
    value,
    error,
    helperText,
    onChange,
    name,
    onClickClear,
    className,
    labelClassName,
  } = props;
  const [katoModalVisible, setKatoModalVisible] = useState<boolean>(false);

  const onClickShowKatoModal = useCallback(() => {
    setKatoModalVisible(true);
  }, []);

  const onCloseKatoModal = useCallback(() => {
    setKatoModalVisible(false);
  }, []);

  const onChangeKato = useCallback(
    (option: SelectOption<Kato>) => {
      if (onChange) {
        onChange(option as SelectOption<Kato>, name as string);
      }
    },
    [name, onChange]
  );

  const onClickClearKato = useCallback(() => {
    if (onClickClear) {
      onClickClear();
    }
  }, [onClickClear]);

  return (
    <>
      <div className={cn('space-y-2', className)}>
        {label && <Label className={cn(labelClassName)}>{label}</Label>}
        <div className="relative">
          <Input
            value={value || ''}
            placeholder={placeholder}
            onClick={onClickShowKatoModal}
            readOnly
            className={cn(
              'cursor-pointer pr-8',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
          />
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
            aria-hidden
          />
          {onClickClear && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
              onClick={onClickClearKato}
            >
              ×
            </Button>
          )}
        </div>
        {helperText && (
          <p
            className={cn(
              'text-sm text-muted-foreground',
              error && 'text-destructive'
            )}
          >
            {helperText}
          </p>
        )}
      </div>
      <KatoModal
        visible={katoModalVisible}
        onChange={onChangeKato}
        onClose={onCloseKatoModal}
      />
    </>
  );
}

export default memo(KatoSelect);
